import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Archive,
  GitBranch,
  Hash,
  Headphones,
  HeartPulse,
  Music,
  Phone,
  RefreshCw,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function levelTone(level) {
  if (level === "critical") return "tag-danger";
  if (level === "warning") return "tag-warning";
  return "tag-online";
}

function moduleLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  return ({
    cliente: "Cliente",
    tronco: "Tronco",
    fila: "Fila",
    agente: "Agente",
    ivr: "URA / IVR",
    did: "DID / DDR",
    anuncio: "Anuncio",
    moh: "Musica de espera",
    gravacao: "Gravacao",
  }[key] || value || "Outros");
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function isEnabled(row) {
  return row?.enabled !== false && row?.ativo !== false && row?.status !== "inativo";
}

function StatusPill({ ok, warning, children }) {
  const klass = ok ? "tag-online" : warning ? "tag-warning" : "tag-danger";
  return <span className={`sbc-tag ${klass}`}>{children}</span>;
}

function SummaryCard({ icon: Icon, label, value, detail, tone = "text-[color:var(--text-primary)]" }) {
  return (
    <div className="sbc-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
          <div className={`mt-2 text-2xl font-mono font-semibold ${tone}`}>{value}</div>
          {detail && <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{detail}</div>}
        </div>
        <Icon size={18} className="text-[color:var(--text-secondary)]" />
      </div>
    </div>
  );
}

function OperationalRow({ icon: Icon, title, status, ok, warning, details = [] }) {
  return (
    <div className="grid gap-3 border-b border-[color:var(--border-default)] px-4 py-3 last:border-b-0 md:grid-cols-[220px_120px_minmax(0,1fr)] md:items-center">
      <div className="flex items-center gap-2 font-medium">
        <Icon size={15} className="text-[color:var(--accent-blue)]" />
        {title}
      </div>
      <StatusPill ok={ok} warning={warning}>{status}</StatusPill>
      <div className="grid gap-2 text-xs text-[color:var(--text-secondary)] sm:grid-cols-2 xl:grid-cols-3">
        {details.map((item) => (
          <div key={item.label} className="flex items-center justify-between gap-3 rounded border border-[color:var(--border-default)] px-3 py-2">
            <span>{item.label}</span>
            <strong className="font-mono text-[color:var(--text-primary)]">{item.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

async function getSafe(path, fallback) {
  try {
    const { data } = await api.get(path, { params: { _ts: Date.now() } });
    return data ?? fallback;
  } catch (err) {
    return fallback;
  }
}

export default function ConfigurationDiagnostics() {
  const { tenants, activeTenantId, user } = useAuth();
  const [data, setData] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeTenant = useMemo(() => {
    const tenantId = String(activeTenantId || user?.tenant_id || "").trim();
    return tenants.find((tenant) => String(tenant.id) === tenantId) || null;
  }, [activeTenantId, tenants, user?.tenant_id]);

  const load = async () => {
    setLoading(true);
    const [
      diagnostics,
      health,
      dids,
      ramais,
      filas,
      ivrs,
      agents,
      moh,
      anuncios,
      registrations,
      recordingStorage,
    ] = await Promise.all([
      getSafe("/diagnostics/configuration", null),
      getSafe("/health/system", null),
      getSafe("/dids", []),
      getSafe("/ramais", []),
      getSafe("/filas", []),
      getSafe("/ivrs", []),
      getSafe("/callcenter-agents", []),
      getSafe("/moh", []),
      getSafe("/anuncios", []),
      getSafe("/registrations", []),
      getSafe("/recording-storage", null),
    ]);
    setData(diagnostics || { summary: {}, by_module: {}, issues: [] });
    setInventory({
      health,
      dids: safeArray(dids),
      ramais: safeArray(ramais),
      filas: safeArray(filas),
      ivrs: safeArray(ivrs),
      agents: safeArray(agents),
      moh: safeArray(moh),
      anuncios: safeArray(anuncios),
      registrations: safeArray(registrations),
      recordingStorage,
    });
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [activeTenantId]);

  const moduleRows = useMemo(() => (
    Object.entries(data?.by_module || {}).sort((a, b) => b[1] - a[1])
  ), [data]);

  const stats = useMemo(() => {
    const inv = inventory || {};
    const dids = safeArray(inv.dids);
    const ramais = safeArray(inv.ramais);
    const filas = safeArray(inv.filas);
    const ivrs = safeArray(inv.ivrs);
    const agents = safeArray(inv.agents);
    const moh = safeArray(inv.moh);
    const anuncios = safeArray(inv.anuncios);
    const registrations = safeArray(inv.registrations);
    const activeDids = dids.filter(isEnabled);
    const enabledRamais = ramais.filter(isEnabled);
    const registeredNumbers = new Set(registrations.map((row) => String(row.user || row.reg_user || row.numero || row.extension || "").trim()).filter(Boolean));
    const registeredRamais = enabledRamais.filter((row) => registeredNumbers.has(String(row.numero || "").trim()));
    const activeQueues = filas.filter(isEnabled);
    const activeIvrs = ivrs.filter(isEnabled);
    const activeAgents = agents.filter(isEnabled);
    const agentsWithQueues = activeAgents.filter((agent) => {
      const assigned = agent.filas || agent.queues || agent.queue_ids || [];
      return Array.isArray(assigned) ? assigned.length > 0 : String(assigned || "").trim();
    });
    return {
      dids,
      activeDids,
      ramais,
      enabledRamais,
      registeredRamais,
      filas,
      activeQueues,
      ivrs,
      activeIvrs,
      agents,
      activeAgents,
      agentsWithQueues,
      moh,
      anuncios,
      registrations,
      recordingStorage: inv.recordingStorage,
      health: inv.health,
    };
  }, [inventory]);

  const summary = data?.summary || {};
  const issues = data?.issues || [];
  const healthy = Number(summary.total || 0) === 0;
  const tenantLabel = activeTenant?.nome || activeTenant?.name || user?.tenant?.nome || user?.tenant_id || "cliente selecionado";

  return (
    <div className="space-y-5" data-testid="configuration-diagnostics-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Diagnostico operacional</div>
          <h1 className="text-2xl font-semibold tracking-tight">Diagnostico por cliente</h1>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">
            {tenantLabel}
          </p>
        </div>
        <button className="sbc-btn" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <SummaryCard icon={ShieldCheck} label="Status geral" value={healthy ? "OK" : "Atencao"} detail="validacao do cliente" tone={healthy ? "text-[color:var(--accent-green)]" : "text-[color:var(--accent-amber)]"} />
        <SummaryCard icon={AlertTriangle} label="Pendencias" value={summary.total || 0} detail={`${summary.critical || 0} criticas`} tone={summary.critical ? "text-[color:var(--accent-red)]" : "text-[color:var(--text-primary)]"} />
        <SummaryCard icon={Hash} label="DIDs ativos" value={stats.activeDids.length} detail={`${stats.dids.length} cadastrados`} />
        <SummaryCard icon={Phone} label="Ramais" value={stats.enabledRamais.length} detail={`${stats.registeredRamais.length} registrados agora`} />
        <SummaryCard icon={Headphones} label="Filas/agentes" value={stats.activeQueues.length} detail={`${stats.activeAgents.length} agentes ativos`} />
      </div>

      <section className="sbc-card overflow-hidden">
        <div className="border-b border-[color:var(--border-default)] p-4">
          <h2 className="font-semibold">Mapa operacional</h2>
          <p className="mt-1 text-xs text-[color:var(--text-muted)]">Resumo rapido dos pontos que mais afetam entrada, saida, atendimento e gravacao.</p>
        </div>
        <OperationalRow
          icon={Hash}
          title="DIDs de entrada"
          status={stats.activeDids.length ? "OK" : "Vazio"}
          ok={stats.activeDids.length > 0}
          warning={!stats.activeDids.length && stats.dids.length > 0}
          details={[
            { label: "Cadastrados", value: stats.dids.length },
            { label: "Ativos", value: stats.activeDids.length },
            { label: "Com destino", value: stats.dids.filter((row) => row.destino || row.destino_id || row.ivr_id || row.fila_id || row.destination_id).length },
          ]}
        />
        <OperationalRow
          icon={Phone}
          title="Ramais"
          status={stats.enabledRamais.length ? "OK" : "Vazio"}
          ok={stats.enabledRamais.length > 0}
          warning={!stats.enabledRamais.length && stats.ramais.length > 0}
          details={[
            { label: "Cadastrados", value: stats.ramais.length },
            { label: "Ativos", value: stats.enabledRamais.length },
            { label: "Registrados", value: stats.registeredRamais.length },
          ]}
        />
        <OperationalRow
          icon={Headphones}
          title="Call center"
          status={stats.activeQueues.length && stats.activeAgents.length ? "OK" : "Revisar"}
          ok={stats.activeQueues.length > 0 && stats.activeAgents.length > 0}
          warning={stats.activeQueues.length > 0 || stats.activeAgents.length > 0}
          details={[
            { label: "Filas ativas", value: stats.activeQueues.length },
            { label: "Agentes ativos", value: stats.activeAgents.length },
            { label: "Agentes com fila", value: stats.agentsWithQueues.length },
          ]}
        />
        <OperationalRow
          icon={Workflow}
          title="URA / IVR"
          status={stats.activeIvrs.length ? "OK" : "Vazio"}
          ok={stats.activeIvrs.length > 0}
          warning={!stats.activeIvrs.length && stats.ivrs.length > 0}
          details={[
            { label: "Cadastradas", value: stats.ivrs.length },
            { label: "Ativas", value: stats.activeIvrs.length },
            { label: "Anuncios", value: stats.anuncios.length },
          ]}
        />
        <OperationalRow
          icon={Music}
          title="Musica e audios"
          status={stats.moh.length || stats.anuncios.length ? "OK" : "Vazio"}
          ok={stats.moh.length > 0 || stats.anuncios.length > 0}
          warning={false}
          details={[
            { label: "Musicas espera", value: stats.moh.length },
            { label: "Anuncios", value: stats.anuncios.length },
            { label: "Padroes ativos", value: stats.moh.filter((row) => row.default || row.is_default || row.padrao).length },
          ]}
        />
        <OperationalRow
          icon={Archive}
          title="Gravacoes"
          status={stats.recordingStorage ? "Configurado" : "Revisar"}
          ok={!!stats.recordingStorage}
          warning={!stats.recordingStorage}
          details={[
            { label: "Storage", value: stats.recordingStorage ? "sim" : "nao" },
            { label: "Chamadas ativas", value: stats.health?.calls?.active ?? "-" },
            { label: "FreeSWITCH", value: stats.health?.freeswitch?.running === false ? "off" : "ok" },
          ]}
        />
        <OperationalRow
          icon={HeartPulse}
          title="Saude do sistema"
          status={stats.health ? "Online" : "Sem leitura"}
          ok={!!stats.health}
          warning={!stats.health}
          details={[
            { label: "CPU/load 1m", value: stats.health?.system?.load?.["1m"] ?? "-" },
            { label: "Disco usado", value: stats.health?.system?.disk?.used_percent != null ? `${stats.health.system.disk.used_percent}%` : "-" },
            { label: "Canais FS", value: stats.health?.calls?.fs_channels ?? "-" },
          ]}
        />
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.8fr)_320px]">
        <section className="sbc-card overflow-hidden">
          <div className="border-b border-[color:var(--border-default)] p-4">
            <h2 className="font-semibold">Pendencias encontradas</h2>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">O resultado respeita o cliente selecionado no topo.</p>
          </div>
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  <th>Nivel</th>
                  <th>Modulo</th>
                  <th>Cliente</th>
                  <th>Item</th>
                  <th>Problema</th>
                  <th>Como corrigir</th>
                </tr>
              </thead>
              <tbody>
                {!issues.length ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-[color:var(--text-muted)]">
                      Nenhuma inconsistencia encontrada.
                    </td>
                  </tr>
                ) : issues.map((issue, index) => (
                  <tr key={`${issue.item_id || issue.item_label || "issue"}-${index}`}>
                    <td><span className={`sbc-tag ${levelTone(issue.level)}`}>{issue.level}</span></td>
                    <td>{moduleLabel(issue.module)}</td>
                    <td>{issue.tenant_name || "-"}</td>
                    <td className="font-mono text-xs">{issue.item_label || issue.item_id || "-"}</td>
                    <td>{issue.message}</td>
                    <td className="text-xs text-[color:var(--text-secondary)]">{issue.hint || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="sbc-card p-4">
          <h2 className="font-semibold">Por modulo</h2>
          <div className="mt-3 space-y-2">
            {!moduleRows.length ? (
              <div className="text-xs text-[color:var(--text-muted)]">Sem problemas por modulo.</div>
            ) : moduleRows.map(([module, count]) => (
              <div key={module} className="flex items-center justify-between rounded border border-[color:var(--border-default)] px-3 py-2 text-sm">
                <span>{moduleLabel(module)}</span>
                <strong className="font-mono">{count}</strong>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded border border-[color:var(--border-default)] p-3">
            <div className="flex items-center gap-2 text-xs font-semibold">
              <Activity size={14} />
              Atualizacao
            </div>
            <div className="mt-2 text-[11px] text-[color:var(--text-muted)]">
              {data?.generated_at ? new Date(data.generated_at).toLocaleString("pt-BR") : "Agora"}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
